import { Component, Input, ChangeDetectorRef, OnInit, ViewChild, ElementRef, OnDestroy, ViewEncapsulation } from '@angular/core';
import { AgentStatsService } from '../services/agent-stats.service';
import { AgentStatIndicator } from '../models/agent-stat.model';
import { Subscription } from 'rxjs';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { AgentStatObject } from '../agent-stat-object';
import { StatService } from '../services/agent-stat.service';
import { CompareToType } from '../agent-stat-enums';
import { WindowService } from 'shared/services/window/window.service';

const RADIUS = 12;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const RADIUS_INSIDE = 9;
const CIRCUMFERENCE_INSIDE = 2 * Math.PI * RADIUS_INSIDE;

@Component({
  selector: 'app-compact-agent-stat',
  templateUrl: './compact-agent-stat.component.html',
  styleUrls: ['./compact-agent-stat.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CompactAgentStatComponent implements OnInit, OnDestroy {
  @Input()
  indicator: AgentStatIndicator;

  @ViewChild('progressValue')
  public progressValue: ElementRef;

  @ViewChild('overPerformValue')
  public overPerformValue: ElementRef;

  @ViewChild('val', { static: true })
  public val: ElementRef;

  @ViewChild('valNum')
  public valNum: ElementRef;

  @ViewChild('valType')
  public valType: ElementRef;

  public showOverPerformCircle: boolean = false;
  public shouldShowBothProgressArrows: boolean = true;
  public indicatorPostFix: string;
  public indicatorShortName: string;
  public isPositiveActionTriggered: boolean = false;

  private _init: AgentStatObject;
  private _targetValue: number;
  private _currentValue: number;
  private _triggersSubscription: Subscription;
  private _indicatorWithoutOrderId;

  constructor(private _changeDetectorRef: ChangeDetectorRef,
              private _agentStatService: AgentStatsService,
              private _statService: StatService,
              private _window: WindowService) {
  }

  async ngOnInit() {
    this._indicatorWithoutOrderId = {...this.indicator};
    delete this._indicatorWithoutOrderId.orderId;

    if (this.indicator.serviceType === bcEnums.services.CHATS) {
      this._init = await this._statService.initStat(this._indicatorWithoutOrderId);
      if (this._init) {
        this._triggersSubscription = this._init.newStatValue$.subscribe((newValues) => {
          const origDiff = this._targetValue - this._currentValue;
          this._currentValue = newValues.agentPerformanceValueToBeCompared;

          if (this.indicator.compareToType !== CompareToType.NONE) {
            if (+this.indicator.compareToValue > 0) {
              this._targetValue = parseInt(this.indicator.compareToValue, 10);
            } else if (+this.indicator.compareToDuration > 0) {
              this._targetValue = newValues.agentPerformanceValueToCompare;
            }
          }
          this._changeDetectorRef.detectChanges();
          if (this.isComparison()) {
            this._showNewProgress();
            const newDiff = this._targetValue - this._currentValue;
            if (newDiff < origDiff) {
              this.isPositiveActionTriggered = true;
              setTimeout(() => { this.isPositiveActionTriggered = false; }, 6000);
            }
          }
          this._scaleFontSize();
        });
      }
    }
    this._getTextsBasedOnIndicatorType();
  }

  ngOnDestroy(): void {
    if (this._triggersSubscription) {
      this._triggersSubscription.unsubscribe();
      this._triggersSubscription = null;
    }
    if (this._init) {
      this._init.cleanUp();
      this._init = null;
    }
  }

  public getCurrentValue(): number {
    return this._currentValue;
  }

  public isComparison(): boolean {
    return this._agentStatService.isComparison(this.indicator);
  }

  public getPerformancePercentage(): number {
    if (this.indicator.compareToValue) {
      return this._agentStatService.getPerformancePercentage(parseInt(this.indicator.compareToValue, 10), this._currentValue);
    }
    return this._agentStatService.getPerformancePercentage(this._targetValue, this._currentValue);
  }

  private _getTextsBasedOnIndicatorType(): void {
    const texts = this._agentStatService.getTextsBasedOnIndicatorType(this.indicator.indicatorType);
    if (texts) {
      this.indicatorPostFix = texts.valuePostFix;
      this.indicatorShortName = texts.shortName;
    }
  }

  private _showNewInsideProgress(): void {
    const overValue = (this._currentValue - this._targetValue) / this._targetValue;
    const dashoffset = CIRCUMFERENCE_INSIDE * (1 - overValue);
    this.overPerformValue.nativeElement.style.strokeDashoffset = dashoffset.toString();
  }

  private _showNewProgress(): void {
    this.progressValue.nativeElement.style.strokeDasharray = CIRCUMFERENCE.toString();
    const progress = this.getPerformancePercentage() / 100;
    const dashoffset = CIRCUMFERENCE * (1 - progress);
    if (progress > 2) {
      this._showFullCircularProgress(this.progressValue);
      this.showOverPerformCircle = true;
      this._changeDetectorRef.detectChanges();
      this._showFullCircularProgress(this.overPerformValue);
      this.shouldShowBothProgressArrows = false;
    } else if (progress > 1) {
      this._showFullCircularProgress(this.progressValue);
      this.showOverPerformCircle = true;
      this._changeDetectorRef.detectChanges();
      this.overPerformValue.nativeElement.style.strokeDasharray = CIRCUMFERENCE_INSIDE.toString();
      this._showNewInsideProgress();
      this.shouldShowBothProgressArrows = false;
    } else {
      this.progressValue.nativeElement.style.strokeDashoffset = dashoffset.toString();
      this.showOverPerformCircle = false;
      this.shouldShowBothProgressArrows = true;
    }
  }

  private _showFullCircularProgress(progressCircle): void {
    progressCircle.nativeElement.style.strokeLinecap = 'square';
    progressCircle.nativeElement.style.strokeDashoffset = '0';
  }

  private _scaleFontSize(): void {
    this._changeDetectorRef.detectChanges();
    const currentValueStyle = this._window.getComputedStyle(this.valNum.nativeElement, null).getPropertyValue('font-size');
    const currentValueFontSize = parseFloat(currentValueStyle);

    if (this.val.nativeElement.scrollWidth > this.val.nativeElement.clientWidth) {
      if (this.isComparison()) {
        const valueTypeStyle = this._window.getComputedStyle(this.valType.nativeElement, null).getPropertyValue('font-size');
        const valueTypeFontSize = parseFloat(valueTypeStyle);
        this.valType.nativeElement.style.fontSize = (valueTypeFontSize * 0.7) + 'px';
      }
      this.valNum.nativeElement.style.fontSize = (currentValueFontSize * 0.7) + 'px';
    }
  }
}
